import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';

// Normalize a stored time (ISO 'T...' or "HH:MM[:SS]") into an <input type=time> value.
function toInputTime(v) {
  if (!v) return '';
  const s = String(v);
  if (s.includes('T')) {
    const d = new Date(s);
    if (!isNaN(d)) {
      const hh = String(d.getUTCHours()).padStart(2, '0');
      const mm = String(d.getUTCMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    }
  }
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (m) return `${m[1].padStart(2, '0')}:${m[2]}`;
  return '';
}

export default function AddTeamScheduleItemModal({
  open, onClose, onSave, onDelete, showToast, teamName, blocks = [], item = null, people = [],
}) {
  const isEdit = !!(item && item.id);
  // Conference is in September — open the calendar there by default.
  const defaultDate = `${new Date().getFullYear()}-09-01`;
  const [date, setDate] = useState(defaultDate);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [activity, setActivity] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  // Multiple linked blocks (newline-joined in storage — titles can contain commas).
  const [linkedBlocks, setLinkedBlocks] = useState([]);
  // One-off person scoping: when set, ONLY these people see the item.
  const [selectedPeople, setSelectedPeople] = useState([]);
  const [personQuery, setPersonQuery] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Prefill (or reset) whenever the modal opens or the edit target changes.
  useEffect(() => {
    if (!open) return;
    if (item) {
      setDate(item.date || defaultDate);
      setStart(toInputTime(item.start));
      setEnd(toInputTime(item.end));
      setActivity(item.activity || '');
      setLocation(item.location || '');
      setDescription(item.description || '');
      setLinkedBlocks(item.linkedBlock ? String(item.linkedBlock).split('\n').map(s => s.trim()).filter(Boolean) : []);
      setSelectedPeople(item.people ? String(item.people).split(',').map(s => s.trim()).filter(Boolean) : []);
    } else {
      setDate(defaultDate);
      setStart(''); setEnd(''); setActivity(''); setLocation(''); setDescription(''); setLinkedBlocks([]);
      setSelectedPeople([]);
    }
    setPersonQuery('');
    setSaving(false);
    setDeleting(false);
  }, [open, item, defaultDate]);

  // Unique, non-empty block titles for the dropdown.
  const blockOptions = Array.from(
    new Set((blocks || []).map(b => (b && b.title ? String(b.title).trim() : '')).filter(Boolean))
  );

  async function handleSave() {
    if (!activity.trim()) { showToast('Item name is required.'); return; }
    if (!start) { showToast('Start time is required.'); return; }
    if (end && end < start) { showToast('End time must be after the start time.'); return; }
    setSaving(true);
    try {
      const record = {
        ...(item || {}),
        id: item && item.id ? item.id : undefined,
        teamId: teamName || '',
        date,
        start,
        end,
        activity: activity.trim(),
        location: location.trim(),
        description: description.trim(),
        linkedBlock: linkedBlocks.join('\n'),
        people: selectedPeople.join(','),
      };
      await onSave(record);
      onClose();
    } catch (err) {
      showToast('Error saving item.');
      console.error(err);
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!isEdit) return;
    setDeleting(true);
    try {
      await onDelete(item.id);
      onClose();
    } catch (err) {
      showToast('Error deleting item.');
      console.error(err);
      setDeleting(false);
    }
  }

  const busy = saving || deleting;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Team Schedule Item' : 'Add Team Schedule Item'}
      footer={
        <>
          {isEdit && (
            <button
              className="btn btn-danger"
              onClick={handleDelete}
              disabled={busy}
              style={{ marginRight: 'auto' }}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </button>
          )}
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSave} disabled={busy}>
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Item')}
          </button>
        </>
      }
    >
      {teamName && (
        <div className="notice-bar" style={{ marginBottom: 4 }}>
          {isEdit ? <>Edits an item on <strong>{teamName}</strong>'s schedule only.</>
            : <>Adds an item to <strong>{teamName}</strong>'s schedule only — it won't affect other teams.</>}
        </div>
      )}
      <div className="form-group">
        <label className="form-label">Date</label>
        <input
          className="form-input"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }} className="form-group">
        <div><label className="form-label">Start Time</label><input className="form-input" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><label className="form-label">End Time</label><input className="form-input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
      </div>
      <div className="form-group"><label className="form-label">Item Name</label><input className="form-input" value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="e.g. Concourse Setup" /></div>
      <div className="form-group"><label className="form-label">Location</label><input className="form-input" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Concourse Level 2" /></div>
      <div className="form-group">
        <label className="form-label">Description <span style={{ fontWeight: 300, color: 'var(--muted)' }}>(optional)</span></label>
        <textarea
          className="form-input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          style={{ resize: 'vertical', minHeight: 70, fontFamily: 'var(--font-b)' }}
        />
      </div>
      <div className="form-group">
        <label className="form-label">Only for specific people <span style={{ fontWeight: 300, color: 'var(--muted)' }}>(optional)</span></label>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginBottom: 8 }}>
          For one-offs — e.g. only Andrew and Nat at a runthrough. When anyone is selected here,
          ONLY those people see this item (block links below are ignored). Leave empty for normal team-wide behavior.
        </div>
        {selectedPeople.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
            {selectedPeople.map(pid => {
              const p = (people || []).find(x => x.id === pid);
              return (
                <span key={pid} className="pill pill-gray" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {p?.name || pid}
                  <button
                    onClick={() => setSelectedPeople(prev => prev.filter(x => x !== pid))}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1, color: 'var(--muted)', fontWeight: 700 }}
                  >
                    ✕
                  </button>
                </span>
              );
            })}
          </div>
        )}
        <div className="search-dropdown">
          <input
            className="form-input"
            placeholder="Type a name to add someone…"
            value={personQuery}
            onChange={e => setPersonQuery(e.target.value)}
            autoComplete="off"
          />
          {personQuery.length > 1 && (
            <div className="search-results">
              {(people || [])
                .filter(p => !selectedPeople.includes(p.id) && (p.name || '').toLowerCase().includes(personQuery.toLowerCase()))
                .slice(0, 8)
                .map(p => (
                  <div key={p.id} className="search-result-item" onClick={() => { setSelectedPeople(prev => [...prev, p.id]); setPersonQuery(''); }}>
                    <div className="search-result-name">{p.name}</div>
                    <div className="search-result-meta">{p.email}</div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
      <div className="form-group" style={{ opacity: selectedPeople.length ? 0.5 : 1 }}>
        <label className="form-label">Link to Blocks <span style={{ fontWeight: 300, color: 'var(--muted)' }}>(optional{selectedPeople.length ? ' — ignored while specific people are set' : ''})</span></label>
        <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginBottom: 8 }}>
          Check one or more blocks this applies to (e.g. a rehearsal that covers several). Leave all unchecked to show it to the whole team.
        </div>
        {/* Include any stale linked blocks no longer in the list so they stay checked. */}
        {Array.from(new Set([...blockOptions, ...linkedBlocks])).length === 0 ? (
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)' }}>
            No blocks yet — this will show to the whole team.
          </div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', maxHeight: 180, overflowY: 'auto' }}>
            {Array.from(new Set([...blockOptions, ...linkedBlocks])).map(title => {
              const checked = linkedBlocks.includes(title);
              return (
                <label
                  key={title}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', background: checked ? 'var(--off)' : 'var(--white)' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setLinkedBlocks(prev => checked ? prev.filter(t => t !== title) : [...prev, title])}
                    style={{ accentColor: 'var(--black)', flexShrink: 0 }}
                  />
                  <span style={{ fontFamily: 'var(--font-b)', fontSize: 13 }}>{title}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    </Modal>
  );
}
